import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaHandPaper,
  FaTrophy,
  FaCoins,
  FaUserShield,
  FaClock,
  FaFire,
  FaChartLine,
  FaQuestionCircle,
  FaChevronDown,
  FaChevronUp,
  FaGem,
  FaArrowRight,
  FaCrown,
} from 'react-icons/fa';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DCKSLAP_DAILY_WITHDRAWAL_LIMIT } from '../../../constants';
import './Howtoplay.css';

const Howtoplay = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqItems = [
    {
      question: "How long does it take to get my UserBadge?",
      answer: "UserBadges are distributed every 24 hours to eligible wallets. Once you own at least 15k $REDDICKS, you'll need to wait for the next badge distribution cycle. Be patient, your badge is coming!"
    },
    {
      question: "Why do I need to deposit slaps one by one?",
      answer: "The goal of DCKSLAP is to generate transactions for the Radix network! Each deposit is a transaction that helps showcase the network's power and scalability. Plus, it's more fun this way - you're actively contributing to the ecosystem's growth."
    },
    {
      question: "When do I receive withdrawal GBOF rewards?",
      answer: "You receive a GBOF reward for every 1,000 claims you make! The requirement is a flat 1,000 withdrawals for each GBOF, making it a consistent goal to aim for."
    },
    {
      question: "Can I lose my UserBadge?",
      answer: "If your $REDDICKS balance drops below 15k, you will keep your badge but won't be eligible to continue withdrawing your daily slaps. Keep your bag filled to maintain your slapping privileges!"
    },
    {
      question: "What can I do with GBOFs?",
      answer: "GBOFs already unlock access to the Reddicks Candy Shop, and they feed directly into VIP Lounge progression. Candy Combiner remains a future utility layer."
    },
    {
      question: "Who should I slap the most?",
      answer: "Piers, obviously! But you can also slap other predefined targets or send to your friends' wallets. The leaderboards track the most active slappers, and top contributors might receive special rewards!"
    }
  ];

  const renderSectionHeader = (eyebrow, title, icon) => (
    <div className="section-header">
      <div className="section-header__icon">{icon}</div>
      <div className="section-header__content">
        <span className="section-header__eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
      </div>
    </div>
  );

  return (
    <div className="howtoplay-page w-full max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-8">
      {/* Introduction */}
      <Card className="border-border overflow-hidden relative">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        <CardContent className="pt-8 pb-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <FaHandPaper className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                How to Play DCKSLAP
              </h1>
              <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                Master the art of slapping in the Radix ecosystem
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started Section */}
      <section className="space-y-6">
        {renderSectionHeader(
          'Phase 01',
          'Getting Started',
          <FaUserShield className="section-header__glyph h-5 w-5" />
        )}

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {/* Eligibility Card */}
          <Card className="info-card eligibility-card bg-card border-border hover:border-border-hover transition-all duration-200 hover:-translate-y-1">
            <CardContent className="pt-6 space-y-4">
              <div className="card-icon-wrapper">
                <FaCoins className="card-icon" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">Eligibility Requirements</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  To participate in DCKSLAP, your wallet must hold at least <Badge variant="secondary" className="font-semibold">15,000 $REDDICKS</Badge> tokens. This proves you're a true believer in the Reddicks revolution!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Badge Card */}
          <Card className="info-card badge-card bg-card border-border hover:border-border-hover transition-all duration-200 hover:-translate-y-1">
            <CardContent className="pt-6 space-y-4">
              <div className="card-icon-wrapper">
                <FaClock className="card-icon" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">Get Your UserBadge</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  UserBadges are distributed <strong className="text-foreground">every 24 hours</strong> to eligible wallets. Once you meet the requirements, wait for the next distribution cycle to receive your badge.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Daily Card */}
          <Card className="info-card daily-card bg-card border-border hover:border-border-hover transition-all duration-200 hover:-translate-y-1">
            <CardContent className="pt-6 space-y-4">
              <div className="card-icon-wrapper">
                <FaHandPaper className="card-icon" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">Daily Withdrawals</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Each UserBadge holder gets <Badge variant="default" className="font-semibold">{DCKSLAP_DAILY_WITHDRAWAL_LIMIT} free withdrawals per day</Badge>. Use them wisely to maximize your impact on the leaderboards!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Core Gameplay Section */}
      <section className="space-y-6">
        {renderSectionHeader(
          'Phase 02',
          'Core Gameplay',
          <FaFire className="section-header__glyph h-5 w-5" />
        )}

        <div className="space-y-4">
          {/* Withdraw Card */}
          <Card className="gameplay-card withdraw-card bg-card border-border hover:border-border-hover transition-all duration-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl text-primary flex items-center gap-2">
                Withdraw Slaps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                Visit the <strong className="text-foreground">Spank Bank</strong> to withdraw your daily slaps. Each withdrawal is a chance to spread the word and climb the leaderboards!
              </p>
              <div className="gameplay-stats flex flex-wrap gap-4 p-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Daily Limit</span>
                  <span className="text-foreground font-semibold">{DCKSLAP_DAILY_WITHDRAWAL_LIMIT} Slaps</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Cost</span>
                  <span className="text-primary font-semibold">Free!</span>
                </div>
              </div>
              <Button
                onClick={() => navigate('/dckslap/slapgenerator')}
                className="w-full sm:w-auto"
              >
                <FaHandPaper className="mr-2 h-4 w-4" />
                Start Withdrawing
              </Button>
            </CardContent>
          </Card>

          {/* Deposit Card */}
          <Card className="gameplay-card deposit-card bg-card border-border hover:border-border-hover transition-all duration-200">
            <CardHeader className="pb-4">
              <CardTitle className="card-title-muted text-xl flex items-center gap-2">
                Deposit Slaps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                Use the <strong className="text-foreground">Deposit Box</strong> to deposit slaps back and earn GBOFs. Each deposit generates a transaction, helping the Radix network thrive!
              </p>
              <div className="gameplay-stats flex flex-wrap gap-4 p-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Reward</span>
                  <span className="text-foreground font-semibold">1 GBOF per 300</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Limit</span>
                  <span className="text-primary font-semibold">No Upper Limit!</span>
                </div>
              </div>
              <div className="deposit-note p-3 rounded-md text-sm text-muted-foreground">
                <strong className="text-foreground">Important:</strong> Deposits are made one at a time to generate maximum network transactions!
              </div>
            </CardContent>
          </Card>

          {/* Send Slaps Card */}
          <Card className="gameplay-card send-card bg-card border-border hover:border-border-hover transition-all duration-200">
            <CardHeader className="pb-4">
              <CardTitle className="card-title-dark text-xl flex items-center gap-2">
                Send Slaps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                The main event! Send your slaps to predefined targets or custom wallets. <strong className="text-foreground">Slap Piers</strong> as much as possible and compete for the top spot!
              </p>
              <div className="gameplay-stats flex flex-wrap gap-4 p-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Targets</span>
                  <span className="text-foreground font-semibold">Piers + More</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Rewards</span>
                  <span className="text-foreground font-semibold">Leaderboard Fame</span>
                </div>
              </div>
              <Button
                variant="secondary"
                onClick={() => navigate('/dckslap/sendslap')}
                className="w-full sm:w-auto"
              >
                <FaHandPaper className="mr-2 h-4 w-4" />
                Start Slapping
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* GBOF Rewards Section */}
      <section className="space-y-6">
        {renderSectionHeader(
          'Phase 03',
          'GBOF Rewards System',
          <FaGem className="section-header__glyph h-5 w-5" />
        )}

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="signal-brief signal-brief--reward max-w-4xl mx-auto">
              <div className="signal-brief__top">
                <Badge variant="outline" className="signal-brief__badge">Reward Currency</Badge>
                <span className="signal-brief__eyebrow">Progression Layer</span>
              </div>
              <h3 className="signal-brief__title">GBOFs mark commitment and unlock the next tier.</h3>
              <p className="signal-brief__body">
                GBOFs (Great Balls of Fire) are earned through deposits and withdrawal milestones.
                They are the progression currency of DCKSLAP, used to level up and unlock higher-tier
                candies and stronger DCKSLAP paths over time.
              </p>
              <div className="signal-brief__points">
                <span className="signal-brief__point">Earned from deposits</span>
                <span className="signal-brief__point">Earned from claim milestones</span>
                <span className="signal-brief__point">Used for tier unlocks</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {/* Deposit Rewards */}
          <Card className="reward-card deposit-reward bg-card border-border hover:border-border-hover transition-all duration-200 hover:-translate-y-1">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary/50">
                  <FaCoins className="icon-muted h-5 w-5" />
                </div>
                <CardTitle className="text-lg">Deposit Rewards</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every <strong className="text-foreground">300 slaps deposited</strong> earns you 1 GBOF. There's no upper limit - keep depositing to stack those GBOFs!
              </p>
              <div className="reward-example p-4 rounded-lg space-y-2">
                <div className="example-row text-sm">
                  <span className="text-muted-foreground">300 deposits</span>
                  <FaArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-foreground font-semibold">1 GBOF</span>
                </div>
                <div className="example-row text-sm">
                  <span className="text-muted-foreground">1,500 deposits</span>
                  <FaArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-foreground font-semibold">5 GBOFs</span>
                </div>
                <div className="example-row text-sm">
                  <span className="text-muted-foreground">3,000 deposits</span>
                  <FaArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-foreground font-semibold">10 GBOFs</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Withdraw Milestones */}
          <Card className="reward-card withdraw-reward bg-card border-border hover:border-border-hover transition-all duration-200 hover:-translate-y-1">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <FaTrophy className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg">Withdraw Milestones</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Earn GBOFs by reaching withdraw milestones. You receive a GBOF for every <strong className="text-foreground">1,000 claims</strong>!
              </p>
              <div className="reward-example p-4 rounded-lg space-y-2">
                <div className="example-row text-sm">
                  <span className="text-muted-foreground">1,000 claims</span>
                  <FaArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-foreground font-semibold">1st GBOF</span>
                </div>
                <div className="example-row text-sm">
                  <span className="text-muted-foreground">2,000 claims</span>
                  <FaArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-foreground font-semibold">2nd GBOF</span>
                </div>
                <div className="example-row text-sm">
                  <span className="text-muted-foreground">3,000 claims</span>
                  <FaArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-foreground font-semibold">3rd GBOF</span>
                </div>
                <div className="example-row text-sm">
                  <span className="text-muted-foreground">4,000 claims</span>
                  <FaArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-foreground font-semibold">4th GBOF</span>
                </div>
              </div>
              <p className="milestone-note text-xs text-muted-foreground text-center">
                Pattern continues every 1,000 claims!
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Live Utility */}
        <Card className="future-utility border-border hover:border-border-hover transition-all duration-200">
          <CardContent className="pt-6">
            <div className="utility-showcase">
              <div className="utility-showcase__header">
                <div className="utility-icon utility-icon--showcase">
                  <FaFire className="h-8 w-8 text-primary" />
                </div>
                <div className="utility-showcase__copy">
                  <span className="utility-showcase__eyebrow">Active Reward Layer</span>
                  <h3 className="utility-showcase__title">GBOF Progression Is Live</h3>
                  <p className="utility-showcase__description">
                    GBOFs already drive progression and unlocks, while DCKSLAPs power the live candy
                    dispenser itself. The reward loop is already active across Candy Shop and VIP Lounge.
                  </p>
                </div>
              </div>

              <div className="utility-status-grid">
                <article className="utility-status-card utility-status-card--live">
                  <div className="utility-status-card__top">
                    <Badge variant="outline" className="utility-status-card__badge">Reddicks Candy Shop</Badge>
                    <span className="utility-status-card__state utility-status-card__state--live">Live Now</span>
                  </div>
                  <div className="utility-status-card__icon">
                    <FaFire className="h-5 w-5" />
                  </div>
                  <p>
                    DCKSLAPs are what you spend in the live candy dispenser to run pulls and earn candies right now.
                  </p>
                </article>

                <article className="utility-status-card utility-status-card--active">
                  <div className="utility-status-card__top">
                    <Badge variant="outline" className="utility-status-card__badge">VIP Lounge</Badge>
                    <span className="utility-status-card__state utility-status-card__state--active">Active</span>
                  </div>
                  <div className="utility-status-card__icon">
                    <FaCrown className="h-5 w-5" />
                  </div>
                  <p>
                    GBOFs are used to level up and unlock higher-tier candies and stronger DCKSLAP paths as you progress.
                  </p>
                </article>

                <article className="utility-status-card utility-status-card--future">
                  <div className="utility-status-card__top">
                    <Badge variant="outline" className="utility-status-card__badge">Candy Combiner</Badge>
                    <span className="utility-status-card__state utility-status-card__state--future">Future Layer</span>
                  </div>
                  <div className="utility-status-card__icon">
                    <FaGem className="h-5 w-5" />
                  </div>
                  <p>
                    The merge path stays reserved for a later release, giving the GBOF reward loop another expansion lane when it is ready.
                  </p>
                </article>
              </div>

              <div className="utility-showcase__actions">
                <Button onClick={() => navigate('/dckslap/candyshop')} className="w-full sm:w-auto">
                  <FaFire className="mr-2 h-4 w-4" />
                  Open Candy Shop
                </Button>
                <Button variant="secondary" onClick={() => navigate('/dckslap/viplounge')} className="w-full sm:w-auto">
                  <FaArrowRight className="mr-2 h-4 w-4" />
                  Visit VIP Lounge
                </Button>
                <div className="utility-showcase__note">
                  Candy Combiner remains intentionally staged as the next release.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* The Mission Section */}
      <section className="space-y-6">
        {renderSectionHeader(
          'Phase 04',
          'The Mission',
          <FaChartLine className="section-header__glyph h-5 w-5" />
        )}

        <Card className="mission-card bg-card border-border hover:border-border-hover transition-all duration-200">
          <CardContent className="pt-6 space-y-6">
            <div className="signal-brief signal-brief--mission max-w-4xl mx-auto">
              <div className="signal-brief__top">
                <Badge variant="outline" className="signal-brief__badge">Network Mission</Badge>
                <span className="signal-brief__eyebrow">Utility Through Play</span>
              </div>
              <h3 className="signal-brief__title">Every slap action doubles as proof of Radix activity.</h3>
              <p className="signal-brief__body">
                DCKSLAP is fun first, but it is also a live transaction engine. Every slap you withdraw,
                deposit, or send creates a transaction on the Radix network, turning gameplay into a visible
                demonstration of Radix DLT throughput, utility, and consistency.
              </p>
              <div className="signal-brief__points">
                <span className="signal-brief__point">Withdraws count</span>
                <span className="signal-brief__point">Deposits count</span>
                <span className="signal-brief__point">Sends count</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="goal-item flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border">
                <FaChartLine className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-foreground">Generate Network Activity</span>
              </div>
              <div className="goal-item flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border">
                <FaTrophy className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-foreground">Showcase Radix Power</span>
              </div>
              <div className="goal-item flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border">
                <FaHandPaper className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-foreground">Have Maximum Fun</span>
              </div>
              <div className="goal-item flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border">
                <FaFire className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-foreground">Slap Piers A LOT</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ Section */}
      <section className="space-y-6">
        {renderSectionHeader(
          'Phase 05',
          'Frequently Asked Questions',
          <FaQuestionCircle className="section-header__glyph h-5 w-5" />
        )}

        <div className="space-y-3">
          {faqItems.map((item, index) => (
            <Card
              key={index}
              className={`faq-item bg-card border-border hover:border-primary/50 transition-all duration-200 cursor-pointer ${expandedFaq === index ? 'expanded border-primary' : ''}`}
            >
              <button
                className="w-full p-4 md:p-6 flex items-center justify-between gap-4 text-left"
                onClick={() => toggleFaq(index)}
                aria-expanded={expandedFaq === index}
              >
                <span className="font-semibold text-foreground text-sm md:text-base">{item.question}</span>
                {expandedFaq === index ? (
                  <FaChevronUp className="h-4 w-4 text-primary flex-shrink-0" />
                ) : (
                  <FaChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
              </button>
              <div className="faq-answer">
                <div className="px-4 md:px-6 pb-4 md:pb-6">
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.answer}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <Card className="cta-card cta-section overflow-hidden relative">
        {/* Top accent glow */}
        <div className="cta-accent-line absolute inset-x-0 top-0 h-px" />
        <CardContent className="pt-10 pb-10 text-center space-y-8">
          <div className="flex flex-col items-center space-y-4">
            <h2 className="cta-title text-2xl md:text-3xl font-bold flex items-center justify-center gap-3">
              <FaFire className="cta-icon h-7 w-7" />
              Ready to Start Slapping?
            </h2>
            <p className="cta-subtitle max-w-xl">
              Get your UserBadge, withdraw your slaps, and join the leaderboards!
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/dckslap/slapgenerator')}
              className="cta-primary-btn gap-2 shadow-lg"
            >
              <FaHandPaper className="h-5 w-5" />
              Start Playing
            </Button>
            <button
              onClick={() => navigate('/dckslap/leaderboard')}
              className="cta-outline-btn"
            >
              <FaTrophy className="h-5 w-5" />
              View Leaderboards
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Howtoplay;
